import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {EpmsAppService} from './services/epms-app.service';
import {GlobalAppData} from './models/global-app-data';
import {OrderService} from './services/order.service';
import {Transaction} from './models/transaction';
import {getQueryParamByName, loadExternalJavascript, loadGtmNoscript} from './common/utils';
import {OscConfigService} from './services/osc-config.service';
import {environment} from '../environments/environment';
import {LoadingService} from './services/loading.service';
import {NgxSpinnerService} from 'ngx-spinner';
import {delay} from 'rxjs/operators';
import {OrderComponent} from './order/order.component';
import {MerchantDetails} from './models/merchant-details';
import {CountryRegionService} from './services/country-region.service';
import { Session } from './models/session';
import { SessionService } from './services/session.service';
import {TransactionStatus} from "./models/transaction-status";
import { faAngleDoubleLeft } from '@fortawesome/free-solid-svg-icons';
import { Constants } from './constants/constants';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SegmentService } from './services/segment.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BrowserEventsModalComponent } from './browser-events-modal/browser-events-modal.component';
import { EventTypes } from './models/enums/event-types';

import { BrowserDetails } from "./models/browser-details";
import {PaymentService} from './services/payment.service';
import { TranslocoService } from '@jsverse/transloco';
import { IndpayComponent } from './indpay/indpay.component';

declare const loadGtmScript: Function;
declare const loadSegment: Function;
declare let analytics: any;

@Component({
  selector: 'app-component',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  paymentType: string = null;
  contact: string = null;
  paymentStatus: string = null;
  orderDetails: Transaction;
  loading = false;
  merchantConfigData: Array<MerchantDetails>;
  @ViewChild(OrderComponent) orderComponent;
  @ViewChild(IndpayComponent) indPayComponent;
  isModeOfReceiptSelectNeeded = false;
  isTransactionValid = true;
  isOscSessionIdValid = true;
  transactionStatus = 'IN_PROGRESS';
  faLeft = faAngleDoubleLeft;
  desktopView = ""
  oscCodes: Array<any> = Constants.OSC_SYSTEM_CODES;
  private sessionExpiryIntervalId: any = null;
  constructor(private appService: EpmsAppService,
              private odService: OrderService,
              private payService: PaymentService,
              private oscConfigService: OscConfigService,
              private gd: GlobalAppData,
              private _loading: LoadingService,
              private spinner: NgxSpinnerService,
              private countryRegionService: CountryRegionService,
              private sessionService: SessionService,
              private breakPointResolver : BreakpointObserver,
              private ss: SegmentService,
              private dialog: MatDialog,
              private route:ActivatedRoute,
              private translocoService: TranslocoService) {
              window.history.pushState(null, '', window.location.href);
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent): void {
    const currentParams = this.route.snapshot.queryParams;

    if (currentParams['ctx'] || currentParams['t']) {
      this.openBackButtonModal();
    } else {
      window.history.pushState(null, '', window.location.href);
    }
  }

  openBackButtonModal(): void {
    const dialogRef = this.dialog.open(BrowserEventsModalComponent, {
      disableClose: true,
      hasBackdrop: true,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result=== 'confirmed') {
        this.ss.track(EventTypes.browserBackOrRefresh);
        this.backToOrder();
        dialogRef.close()
      }
    });
  }


  ngOnInit(): void {
    this.breakPointResolver.observe('(min-width : 1100px)')
      .subscribe(result=>{
        if(result.matches){
          this.desktopView = "desktop"
        }
    })

    this.breakPointResolver.observe('(max-width : 1099px)')
      .subscribe(result=>{
        if(result.matches){
          this.desktopView = "mobile"
        }
    })
    this.listenToLoading();
    this.loadGtm();
    const gdSessionData: GlobalAppData = this.appService.getSessionDetails();
    const challengeTxnStatus = (getQueryParamByName('status') || null);
    if (challengeTxnStatus) {
      this.paymentStatus = challengeTxnStatus.toLocaleLowerCase() === 'approved' ? 'approved' : 'declined';
      // 3DS return — ctx is in the URL, re-validate session and get transactionId
      const ctxFrom3ds = getQueryParamByName('ctx');
      if (ctxFrom3ds) {

        // Restore gd from sessionStorage so paymentType='cc' survives the 3DS redirect reload
        const savedGd = sessionStorage.getItem('gd');
        if (savedGd) {
          try {
            const restoredGd = JSON.parse(Buffer.from(savedGd, 'base64').toString('utf8'));
            Object.assign(this.gd, restoredGd);
            this.paymentType = this.gd.paymentType;
            console.log(`[3DS_RESUME] gd restored, paymentType=${this.paymentType}`);
          } catch (e) {
            console.error('[3DS_RESUME] Could not restore gd from sessionStorage', e);
          }
        }

        this.odService.resumePayment(ctxFrom3ds).subscribe({
          next: ({ sessionId, transactionId }) => {
            console.log(`[3DS_RESUME] session restored transactionId=${transactionId}`);
            this.gd.transId = transactionId;
            this.loadCountries();
            this.manageSession(sessionId);
            // Load order and start session management — same as normal ctx flow
            setTimeout(() => this.getOrderDetails(), 200);
          },
          error: (err) => {
            console.error('[3DS_RESUME_FAILED] Could not resume session after 3DS', err);
            this.isTransactionValid = false;
          }
        });
        return;
      }
    }
    if (gdSessionData) {
      this.paymentType = gdSessionData.paymentType;
      this.contact = gdSessionData.address;
      this.orderDetails = gdSessionData.orderTransaction;
      Object.assign(this.gd, gdSessionData);

      // Css file loading based on Osc Code
      this.addStyleSheetbyOscCode(this.orderDetails?.originatingSystemCode);

      this.isModeOfReceiptSelectNeeded = false;
      this.countryRegionService.countryList = JSON.parse(sessionStorage.getItem('countries'));
      this.loadApiKeys(this.orderDetails?.originatingSystemCode);
      this.changeTofelBGColor(this.gd.oscConfig.oscCode);
      if(this.gd.oscConfig && this.gd.oscConfig.maxRejectAtmptCount) {
        environment.MAX_PAYMENT_ALLOWED_ATTEMPTS = this.gd.oscConfig.maxRejectAtmptCount;
      }
      this.loadSegmentConfig();
    } else {
      this.gd.attemptCount = sessionStorage.getItem('page') ? Number(sessionStorage.getItem('page')) : 1;
      this.gd.isStubEnabled = this.isStubEnabled();
      

      const ctx = getQueryParamByName('ctx');
      if (ctx) {
        // NEW FLOW — exchange ctx for secure cookies, get transactionId back
        // ngOnInit normal flow
        this.odService.getContextBody(ctx).subscribe({
          next: ({ sessionId, transactionId }) => {
            this.gd.transId = transactionId;
            // cookies are now set — safe to call session-dependent endpoints
            setTimeout(() => this.getOrderDetails(), 200);
            this.loadCountries();
            this.manageSession(sessionId);
          },
          error: (err) => {
            console.error('[CTX_EXCHANGE_FAILED] Could not resolve ctx — session expired or replayed', err);
            this.isTransactionValid = false;
          }
        });
      } else {
        // FALLBACK — existing flow for page reloads that already have session in storage
        console.error('[CTX_MISSING] No ctx param found in URL — redirecting to error page');
        this.isTransactionValid = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.sessionService.sessionExpiredSubject.unsubscribe();
    if (this.sessionExpiryIntervalId) {
      clearInterval(this.sessionExpiryIntervalId);
    }
    window.sessionStorage.clear();
  }

  isShowPage(){
    if(!this.isModeOfReceiptSelectNeeded && this.isTransactionValid && this.isOscSessionIdValid){
      return true;
    }else {
      return false;
    }
  }

  // OSC based dynamic Styles
  addStyleSheetbyOscCode(oscCode) {
    let headID = document.getElementsByTagName('head')[0];
    let link = document.createElement('link');
    link.type = 'text/css';
    link.rel = 'stylesheet';
    link.id = 'widget_styles';
    headID.appendChild(link);
    if(this.oscCodes.includes(oscCode)){
      link.href = './assets/css/ERT.css';
    } else {
      link.href = './assets/css/default.css';
    }
  }

    // Customer Device Details updating in order
    updateCustomerDeviceDetails(orderId: string){
      const custBrowserDetails = new BrowserDetails();
      custBrowserDetails.userAgent = navigator.userAgent;
      custBrowserDetails.browserLanguage = navigator.language;
      custBrowserDetails.epmsOrderId = orderId;
      this.payService.updateCustomerBrowserDetails(custBrowserDetails).subscribe({next:(result: string) => {
          console.log("Browser Details has updated", result);
        },
        error: (error: any) => {
          console.log("Browser Details call failed", error);
        }
      });
    }

  backToOrder() {
    if ('VTHOP' === this.gd?.orderTransaction?.order?.hopIndicator) {
      const url = `${environment.VERTUAL_TERIMINAL_URL}?t=${this.gd.transId}&status=BACKTOORDER&sourceType=${this.gd.orderTransaction.sourceType}`;
      location.href = url;
    } else if(this.isIndpayEnable()){
      if(this.gd.isStubEnabled){
          this.indPayComponent.indPayStubComponent.merchantResponseComponent.backToOrder('backToOrder', '', {});
      }else{
          this.indPayComponent.merchantResponseComponent.backToOrder('backToOrder', '', {});
      }
    }else{
      this.orderComponent.merchantResponseComponent.backToOrder('backToOrder', '', {});
    }
  }

  redirectToTimeoutURL(){
    if(this.isIndpayEnable()){
      if(this.gd.isStubEnabled){
         this.indPayComponent.indPayStubComponent.merchantResponseComponent.redirectToTimeoutURL('', {});
      }else{
         this.indPayComponent.merchantResponseComponent.redirectToTimeoutURL('', {});
      }
    }else{
      this.orderComponent.merchantResponseComponent.redirectToTimeoutURL('', {});
    }
  }

  private listenToLoading(): void {
    this._loading.loadingSub
      .pipe(delay(0)) // This prevents a ExpressionChangedAfterItHasBeenCheckedError for subsequent requests
      .subscribe((loading) => {
        this.loading = loading;
        this.spinner.show();
      });
  }

  private changeTofelBGColor(oscCode:string):void{
    if(this.oscCodes.includes(oscCode)){
      document.documentElement.style.setProperty('--tofel-background-color', '#F3F4FD');
    }
  }

  private getOrderDetails(): void {
    this._loading.setLoading(true,'internal');
    this.odService.getOrder(this.gd.transId).subscribe({next:(result: Transaction) => {      
      if (result) {
        this.orderDetails = result;
        this.gd.orderTransaction = result;
        this.gd.sourceType = result.order.sourceType;
        this.gd.subscriptionDetail = result.subscriptionDetail;
        this.changeTofelBGColor(result.originatingSystemCode);
        this.addStyleSheetbyOscCode(result.originatingSystemCode);
        let curUserlang=this.gd.orderTransaction.order.displayLanguage?
                this.gd.orderTransaction.order.displayLanguage.toLowerCase():"en";
        if(window.sessionStorage.getItem('curUserLan')!==null){
          this.translocoService.setActiveLang(window.sessionStorage.getItem('curUserLan'));
        }else{
          window.sessionStorage.setItem('curUserLan',curUserlang);
          this.translocoService.setActiveLang(curUserlang);
        }
        // Get Order Details
        if(this.orderDetails.order?.custIpAddr == null && this.orderDetails.order?.browserTyp == null){
          this.updateCustomerDeviceDetails(this.orderDetails.order?.epmsOrderId);
        }

        this.checkTransactionStatus();
        if (result.modeOfReceipt !== 'N'  && !this.gd.modeOfReceiptConfirmed) {
          this.isModeOfReceiptSelectNeeded = true;
          this._loading.setLoading(false,'internal');
        } else {
          this.loadOscConfigDetails();
        }
      }
      
    },error: (error: any) => {
      this._loading.setLoading(false,'internal');
    }});
  }

  private checkTransactionStatus() {
    if (this.orderDetails && this.orderDetails.decision
      && this.transactionStatus !== 'IN_PROGRESS'
      && (this.orderDetails.decision.toLocaleLowerCase() === 'accept'
      || this.orderDetails.decision.toLocaleLowerCase() === 'reject'
      || this.orderDetails.decision.toLocaleLowerCase() === 'error'
      || this.orderDetails.decision.toLocaleLowerCase() === 'decline')
    ) {
      this.isTransactionValid = false;
    }
  }

  private setSessionExpiryInterval(milliseconds: number) {
    this.sessionExpiryIntervalId = setInterval(() => {
      if(this.transactionStatus !== 'COMPLETE') {
        const transactionStatus = new TransactionStatus();
        transactionStatus.orderTransactionId = this.gd.transId;
        transactionStatus.pgmResponseCode = '151';
        transactionStatus.pgmResponseMessage = 'SESSION TIMEOUT';
          transactionStatus.decision = 'TIMEOUT';
          transactionStatus.transactionStatus = 'INCOMPLETE';
        const obs = this.odService.updateTransactionStatus(transactionStatus);
        obs.subscribe({next:() => {
          console.log('timed out');
          this.redirectToTimeoutURL()
        }, error:error => {
          console.log('error updating tx status', error);
          if (error?.status === 408) {
            // Genuine session expiry — Redis key gone, cookies were still valid.
            console.warn('[SESSION_EXPIRED] Redis session TTL expired — genuine timeout, redirecting');
            clearInterval(this.sessionExpiryIntervalId);
            this.redirectToTimeoutURL();
          } else if (error?.status === 401) {
            // Cookies missing — only happens because payment already completed and cookies were cleared.
            console.warn('[SESSION_ALREADY_CLOSED] Cookies cleared — payment already completed, showing invalid-transaction message');
            this.isTransactionValid = false;
            clearInterval(this.sessionExpiryIntervalId);
          }
        }});
      }
    }, milliseconds)
  }

private manageSession(sessionId: string): void {
    this.sessionService.get(sessionId)
    .subscribe({next:(session: Session) => {
      this.transactionStatus = session?.transactionStatus;
      if(session.balanceTTLInSeconds > 0) {
        this.sessionService.sessionId = sessionId;
        this.sessionService.startCheckingSessionExpiry = true;
        this.sessionService.sessionExpiredSubject
          .subscribe((isSessionExpired: boolean) => {
            if(isSessionExpired) {
              this.redirectToTimeoutURL()
            }
          });
        this.setSessionExpiryInterval(session.balanceTTLInSeconds * 1000);
      } else if (this.transactionStatus !== 'COMPLETE') {
          this.redirectToTimeoutURL()
      }
      
    },error:error => {
      this.isOscSessionIdValid=false;
    }});
  }

  private loadPaypalConfig(): void{
    const paypalClientId = this.gd.apiKeys?.paypalClientId;
    if (paypalClientId)
      loadExternalJavascript(environment.PAYPAL_SDK + paypalClientId);
    else
      console.log('Not able to load Paypal ClientId source');
  }

  private loadGtm() {
    try {
      loadGtmScript(environment.GTM_APP_ID);
      loadGtmNoscript(environment.GTM_APP_ID);
    } catch(e) {
      console.error('exception thrown', e);
    }
  }

  private loadApiKeys(oscCode: string): void {
    this.oscConfigService.getOscApiKeys(oscCode)
      .subscribe({
        next: (apiKeys) => {
          this.gd.apiKeys = apiKeys;
          console.log('OSC API keys loaded:', JSON.stringify(apiKeys));
          if (!this.gd.isStubEnabled && apiKeys?.paypalClientId) {
            this.loadPaypalConfig();
          }
        },
        error: (err) => {
          console.error('Error fetching OSC API keys', err);
        }
      });
  }

  private loadOscConfigDetails(): void {
    const oscConfigObs = this.oscConfigService.getOscConfigForHop(
      this.orderDetails.originatingSystemCode,
      this.orderDetails.order.hopIndicator
    );
    oscConfigObs.subscribe(data => {
      this.gd.oscConfig = data;
      this._loading.setLoading(false, 'internal');

      // Fetch API keys — stored in gd.apiKeys, available to all components
      this.loadApiKeys(this.orderDetails.originatingSystemCode);

      if (data && data.maxRejectAtmptCount) {
        environment.MAX_PAYMENT_ALLOWED_ATTEMPTS = data.maxRejectAtmptCount;
      }
      if (data && data.merchantDefinedField) {
        this.merchantConfigData = data.merchantDefinedField;
      }
      this.loadSegmentConfig();
      this.redirectForOscSessionExists();
    });
  }

  private redirectForOscSessionExists(): void {
    const oscSessionRedirect = getQueryParamByName('oscSessionRedirect');
    console.log(`oscSessionRedirect = ${oscSessionRedirect}`);
    // eslint-disable-next-line eqeqeq
    if (oscSessionRedirect == 'true') {
      this.orderComponent.merchantResponseComponent.redirectToMerchantSuccessURL('osc', {}, 'redirectForOscSessionExists');
    }
  }

  private isStubEnabled(): boolean {
    const isStubParamExists = getQueryParamByName('isStub');
    if (isStubParamExists) {
      return (isStubParamExists === 'true');
    }
    return false;
  }

  private loadCountries(): void {
    const countries = sessionStorage.getItem('countries');
    if (!countries) {
      this.countryRegionService.loadCountries();
    } else {
      this.countryRegionService.countryList = JSON.parse(countries);
    }
  }

  onModeOfReceiptSelect(event): void {
    this.orderDetails.modeOfReceipt = event.target.value;
    this.oscConfigService.updateModeOfReceipt(this.gd.transId, event.target.value).subscribe({next:data => {
      this.isModeOfReceiptSelectNeeded = false;
      this.gd.modeOfReceiptConfirmed = true;
      this.loadOscConfigDetails();
    }, error:error => {
      console.error(`Error updating the Mode of recipt. Message ${JSON.stringify(error)}`);
      this.isModeOfReceiptSelectNeeded = true;
    }});
  }

  private loadSegmentConfig(): void {
    const oscConfig = this.gd.oscConfig;
    if (oscConfig && oscConfig.sendSegmentEventIndicator && oscConfig.marketingAnalysisIdUi) {
      loadSegment(oscConfig.marketingAnalysisIdUi);
      analytics?.ready(() => {
        this.ss.identify();
      });
    }
  }

  isIndpayEnable(){
    if(!this.isInOscEnabled()){
      return false;
    }
    return this.gd?.oscConfig?.enableRazorpayIndicator;
  }

  private isInOscEnabled(){
    return this.gd?.oscConfig?.applicationName===Constants.IN_APP_NAME;
  }

  getCurrencyCode(){
    return this.gd?.orderTransaction?.amountDetail?.baseCurrencyCode
  }

  getCurrencySymbol(){
    if(this.gd?.orderTransaction?.amountDetail?.baseCurrencyCode==="INR"){
      return "₹";
    }
    return "$";
  }

}
